import { useQuery } from '@tanstack/react-query';
import { format, parse } from 'date-fns';

import { useTeamMembers } from '../../../hooks/useTeamMembers';
import { useTeam } from '../contexts/TeamContext';
import {
  fetchCheckIns,
  fetchMeetings,
  fetchOOOData,
} from '../services/timeline';
import {
  TeamMemberTimeline,
  TimelineItem,
  SessionType,
  CheckInResponse,
  MeetingResponse,
  TimelineDataResponse,
  CheckInsResponse,
  MeetingsResponse,
} from '../types/timeline';
import {
  transformMeeting,
  calculateTotalMeetingDuration,
  getOOOItem,
  getTimeInTeamTimezone,
} from '../utils/timelineUtils';

export function useTimelineData(
  date: Date,
  selectedMembers: { memberId: string; employeeId: string }[],
): TimelineDataResponse {
  const { team } = useTeam();
  const { data: teamMembers } = useTeamMembers();
  const dateStr = format(date, 'yyyy-MM-dd');

  // Get team schedule times
  const checkInTime1 = team?.check_in_times?.[0] || '12:30';
  const checkInTime2 = team?.check_in_times?.[1] || '17:30';

  const { data: checkIns, isLoading: isLoadingCheckIns } =
    useQuery<CheckInsResponse>({
      queryKey: ['checkIns', selectedMembers, dateStr],
      queryFn: () =>
        fetchCheckIns(
          selectedMembers.map((member) => member.memberId),
          dateStr,
        ),
      enabled: selectedMembers.length > 0,
    });

  const { data: meetings, isLoading: isLoadingMeetings } =
    useQuery<MeetingsResponse>({
      queryKey: ['meetings', selectedMembers, dateStr],
      queryFn: () =>
        fetchMeetings(
          selectedMembers.map((member) => member.memberId),
          dateStr,
        ),
      enabled: selectedMembers.length > 0,
    });

  const { data: oooIds } = useQuery({
    queryKey: ['ooo', selectedMembers, dateStr],
    queryFn: () => fetchOOOData(selectedMembers, dateStr),
    enabled: selectedMembers.length > 0,
  });
  /**
   * Gets the time range for a specific session based on team's schedule
   */
  const getSessionTimeRange = (
    session: SessionType,
  ): { start: Date; end: Date } => {
    const baseDate = date;
    switch (session) {
      case 'session_1':
        return {
          start: new Date(baseDate.setHours(0, 0, 0, 0)), // Start of day
          end: parse(checkInTime1, 'hh:mm a', baseDate),
        };
      case 'session_2':
        return {
          start: parse(checkInTime1, 'hh:mm a', baseDate),
          end: parse(checkInTime2, 'hh:mm a', baseDate),
        };
      case 'session_3':
        return {
          start: parse(checkInTime2, 'hh:mm a', baseDate),
          end: parse('23:59:59', 'HH:mm:ss', baseDate),
        };
      default:
        return {
          start: new Date(baseDate.setHours(0, 0, 0, 0)), // Start of day
          end: parse('23:59:59', 'HH:mm:ss', baseDate),
        };
    }
  };

  /**
   * Constructs timeline items for a specific session
   */
  const constructTimelineSession = (
    memberCheckIns: CheckInResponse[],
    memberMeetings: MeetingResponse[],
    session: SessionType,
  ): TimelineItem[] => {
    const items: TimelineItem[] = [];
    const sessionRange = getSessionTimeRange(session);
    const sessionNumber = parseInt(session.split('_')[1], 10);

    // Process meetings first
    const sessionMeetings = memberMeetings?.filter((meeting) => {
      const meetingStart = getTimeInTeamTimezone(meeting.start_time);
      const meetingEnd = getTimeInTeamTimezone(meeting.end_time);
      const isOOO =
        meeting.title.toLowerCase().includes('ooo') ||
        meeting.title.toLowerCase().includes('out of office');

      if (isOOO) {
        // For OOO events, check for any overlap with session
        return (
          (meetingStart >= sessionRange.start &&
            meetingStart < sessionRange.end) || // Starts in session
          (meetingEnd > sessionRange.start && meetingEnd <= sessionRange.end) || // Ends in session
          (meetingStart <= sessionRange.start && meetingEnd >= sessionRange.end) // Spans across session
        );
      }
      if (
        (meeting.title.toLowerCase().includes('home') ||
          meeting.title.toLowerCase().includes('office')) &&
        meetingEnd.getTime() - meetingStart.getTime() >= 24 * 60 * 60 * 1000
      ) {
        return false;
      }
      // For regular meetings, only check start time
      return (
        meetingStart >= sessionRange.start && meetingStart < sessionRange.end
      );
    });

    // Check for OOO first
    const hasOOO = sessionMeetings?.some(
      (meeting) =>
        meeting.title.toLowerCase().includes('ooo') ||
        meeting.title.toLowerCase().includes('out of office'),
    );

    if (sessionMeetings?.length > 0) {
      const oooMeeting = sessionMeetings.find(
        (meeting) =>
          meeting.title.toLowerCase().includes('ooo') ||
          meeting.title.toLowerCase().includes('out of office'),
      );

      if (oooMeeting) {
        items.push({
          type: 'OOO',
          id: `ooo-${oooMeeting.id}`,
          reason: oooMeeting.title,
        });
      } else {
        items.push({
          type: 'MEETINGS',
          id: `meetings-${session}`,
          meetings: sessionMeetings.map(transformMeeting),
          totalDurationMinutes: calculateTotalMeetingDuration(sessionMeetings),
        });
      }
    }

    // Only process check-ins for sessions 1 and 2
    if (session !== 'session_3') {
      // Process check-ins
      const sessionCheckIn = memberCheckIns?.find(
        (checkIn) => checkIn.daily_sequence === sessionNumber,
      );

      if (sessionCheckIn) {
        sessionCheckIn.ticket_check_ins.forEach((checkIn) => {
          if (checkIn.check_in_type === 'ticket' && checkIn.ticket) {
            items.push({
              type: 'ticket',
              id: `ticket-${checkIn.id}`,
              ticketId: checkIn.ticket.id,
              title: checkIn.title,
              description: checkIn.description,
              progress: {
                current: checkIn.completion_percentage,
                previous: checkIn.previous_completion_percentage,
              },
              blockers: checkIn.blockers,
              wipLinks: checkIn.wip_links,
              checkInId: sessionCheckIn.id,
              externalLink: checkIn.ticket.external_link,
            });
          } else if (checkIn.check_in_type === 'adhoc_task') {
            items.push({
              type: 'adhoc_task',
              id: `adhoc-${checkIn.id}`,
              checkInId: sessionCheckIn.id,
              title: checkIn.title,
              description: checkIn.description,
            });
          }
        });
      } else if (!hasOOO) {
        // Only add missed check-in if not OOO
        items.push({
          type: 'MISSED_CHECKIN',
          id: `missed-${session}`,
        });
      }
    }

    return items;
  };

  const timelineData: Record<string, TeamMemberTimeline> =
    teamMembers
      ?.filter((member) =>
        selectedMembers.some(
          (selectedMember) => selectedMember.memberId === member.id,
        ),
      )
      .reduce((acc, member) => {
        const memberCheckIns =
          checkIns?.data?.filter((checkIn) => checkIn.user_id === member.id) ||
          [];
        const memberMeetings =
          meetings?.data?.filter((meeting) => meeting.user_id === member.id) ||
          [];
        const onLeave =
          oooIds && oooIds?.filter((id) => id === member.id)?.length > 0;
        acc[member.id] = {
          memberId: member.id,
          firstName: member.firstName,
          lastName: member.lastName,
          avatarUrl: member.avatarUrl,
          sessions: {
            session_1: {
              items: onLeave
                ? getOOOItem(member.id)
                : constructTimelineSession(
                    memberCheckIns,
                    memberMeetings,
                    'session_1',
                  ),
            },
            session_2: {
              items: onLeave
                ? getOOOItem(member.id)
                : constructTimelineSession(
                    memberCheckIns,
                    memberMeetings,
                    'session_2',
                  ),
            },
            session_3: {
              items: onLeave
                ? getOOOItem(member.id)
                : constructTimelineSession(
                    memberCheckIns,
                    memberMeetings,
                    'session_3',
                  ),
            },
          },
        };
        return acc;
      }, {} as Record<string, TeamMemberTimeline>) || {};

  return {
    data: timelineData,
    isLoading: isLoadingCheckIns || isLoadingMeetings,
  };
}
